/**
 * inventoryValuationDataSource.ts — WatermelonDB → InventoryValuationInput bridge.
 *
 * Loads every physical, inventory-tracked product (including ones now empty, in
 * case they held stock at the date), and sums the canonical signed delta of
 * each product's movements strictly after `asOf`. The builder rewinds current
 * stock by that sum.
 */

import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { fetchInChunks } from '@/db/fetchInChunks';
import { Product, Category, StockMovement } from '@/db/models/Inventory';
import { productStockDelta } from '@/sync/liveStock';
import type { InventoryValuationInput } from './inventoryValuation';

export async function loadInventoryValuation(
  storeId: string,
  asOf: Date,
): Promise<InventoryValuationInput> {
  const products = await database.get<Product>('products').query(
    Q.where('store_id', storeId),
    Q.where('is_deleted', false),
    Q.where('is_service', false),
    Q.where('track_inventory', true),
  ).fetch();

  const productIds = products.map((p) => p.id);

  // Category names.
  const categoryIds = [...new Set(products.map((p) => p.categoryId).filter((id): id is string => !!id))];
  const categoryRows = await fetchInChunks(categoryIds, (batch) =>
    database.get<Category>('categories').query(Q.where('id', Q.oneOf(batch)), Q.where('is_deleted', false)).fetch(),
  );
  const categoryName = new Map(categoryRows.map((c) => [c.id, c.name]));

  // Movements strictly after asOf, summed per product as canonical deltas.
  const movements = await fetchInChunks(productIds, (batch) =>
    database.get<StockMovement>('stock_movements').query(
      Q.where('product_id', Q.oneOf(batch)),
      Q.where('is_deleted', false),
      Q.where('created_at', Q.gt(asOf.getTime())),
    ).fetch(),
  );
  const deltaAfter = new Map<string, number>();
  for (const m of movements) {
    deltaAfter.set(m.productId, (deltaAfter.get(m.productId) ?? 0) + productStockDelta(m.type, m.quantity));
  }

  return {
    asOf,
    products: products.map((p) => ({
      id: p.id,
      name: p.name,
      category: (p.categoryId && categoryName.get(p.categoryId)) || 'Uncategorised',
      currentStock: p.currentStock,
      purchasePrice: p.purchasePrice,
      sellingPrice: p.sellingPrice,
      deltaAfter: deltaAfter.get(p.id) ?? 0,
    })),
  };
}
